import {
  compose,
  lifecycle,
  withHandlers,
  withState,
  defaultProps,
} from 'recompose';
import { connect } from 'react-redux';

import { getCompanies } from 'actions/companies';
import { showLoading, hideLoading } from 'actions/ui';
import { setFilters, clearFilters } from 'actions/redirect';

import { wrapWithSideEffect } from 'utils';
import { hideIfNoData } from 'utils/enhancers';

import CompaniesListPage from './CompaniesListPage';

const PAGING_LIMIT = 40;

const defaultParams = {
  sortName: 'id',
  sortDirection: 'desc',
};

const mapStateToProps = ({ redirect }) => ({
  filters: redirect.filters,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
  onInit: (args) => dispatch(getCompanies(args)),
  showLoading: () => dispatch(showLoading()),
  hideLoading: () => dispatch(hideLoading()),
  setFilters: (args) => dispatch(setFilters(ownProps.location, args)),
  clearFilters: () => dispatch(clearFilters()),
});

const wrapWithProgress = (props) => (promise) => {
  return wrapWithSideEffect(props.showLoading)(promise).finally(
    props.hideLoading
  );
};

const state = compose(
  withState('initialData', 'setInitialData'),
  withState('sortName', 'setSortName', defaultParams.sortName),
  withState('sortDirection', 'setSortDirection', defaultParams.sortDirection),
  withState('search', 'setSearch', null),
  withState('searchParam', 'setSearchParam'),
  withState('page', 'setPage', 0),
  defaultProps({
    pageLimit: PAGING_LIMIT,
  })
);

const refresh = (props) => {
  const {
    sortName,
    sortDirection,
    search,
    searchParam,
    page,
    pageLimit,
    onInit,
    setInitialData,
  } = props;
  return wrapWithProgress(props)(
    onInit({
      sortName,
      sortDirection,
      search,
      searchParam,
      page,
      pageLimit,
    }).then(setInitialData)
  );
};

const handlers = withHandlers({
  onInit: (props) => () => {
    const { location, filters } = props;
    if (location && filters && location.pathname !== filters.url) {
      props.clearFilters();
      return refresh(props);
    }
    const {
      sortName,
      sortDirection,
      search,
      searchParam,
      page,
    } = props.filters;
    if (sortName) props.setSortName(sortName);
    if (sortDirection) props.setSortDirection(sortDirection);
    if (search) props.setSearch(search);
    if (searchParam) props.setSearchParam(searchParam);
    if (page) props.setPage(page);
    return refresh({
      ...props,
      sortName: sortName || defaultParams.sortName,
      sortDirection: sortDirection || defaultParams.sortDirection,
      search,
      searchParam,
      page: page || 0,
    });
  },
  onSort: (props) => ({ sortName, sortDirection }) => {
    props.setPage(0);
    props.setSortName(sortName);
    props.setSortDirection(sortDirection);
    props.setFilters({
      sortName,
      sortDirection,
      search: props.search,
      searchParam: props.searchParam,
      page: 0,
    });
    return refresh({ ...props, sortName, sortDirection, page: 0 });
  },
  onPage: (props) => ({ page }) => {
    props.setPage(page);
    props.setFilters({
      sortName: props.sortName,
      sortDirection: props.sortDirection,
      search: props.search,
      searchParam: props.searchParam,
      page,
    });
    return refresh({ ...props, page });
  },
  onSearch: (props) => ({ search, searchParam }) => {
    props.setPage(0);
    props.setSearch(search);
    props.setSearchParam(searchParam.value);
    props.setFilters({
      sortName: props.sortName,
      sortDirection: props.sortDirection,
      search,
      searchParam: searchParam.value,
      page: props.page,
    });
    return refresh({
      ...props,
      search,
      searchParam: searchParam.value,
      page: 0,
    });
  },
});

const retrieveData = lifecycle({
  componentDidMount() {
    this.props.onInit();
  },
});

export default compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  state,
  handlers,
  retrieveData,
  hideIfNoData((props) => !props.initialData)
)(CompaniesListPage);
